import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { KeyRound, Pencil, Search, ShieldCheck, Trash2, UserRound, X } from 'lucide-react';
import AdminSection from '../components/AdminSection';
import Pagination from '../components/Pagination';
import { useServerPaged } from '../hooks/use-paged';
import {
  deleteCustomer,
  fetchCustomers,
  fetchCustomersFromBookings,
  setCustomerPassword,
  updateCustomer,
} from '../lib/adminApi';
import { getAuthSession } from '../lib/api';

const inputCls =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-500 outline-none transition focus:border-[#1fa5a3] focus:ring-4 focus:ring-[#1fa5a3]/10';

const eur = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v || 0);

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function AdminCustomers() {
  const queryClient = useQueryClient();
  const me = getAuthSession().user;
  // 'registered' = db.users accounts (editable, has GDPR/password actions).
  // 'all' = every real paying customer, aggregated from bookings.customer —
  // most of them are guests with no account at all (checkout doesn't require login).
  const [view, setView] = useState('registered');
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const [pwUser, setPwUser] = useState(null);
  const [password, setPassword] = useState('');
  const [delUser, setDelUser] = useState(null);
  const [delMode, setDelMode] = useState('anonymize');
  const [delConfirm, setDelConfirm] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', email: '', phone: '', role: 'user', is_active: true });

  const startEdit = (u) => {
    setEditUser(u);
    setEditForm({
      full_name: u.full_name || '',
      email: u.email || '',
      phone: u.phone || '',
      role: u.role || 'user',
      is_active: u.is_active !== false,
    });
    setMsg({ ok: '', err: '' });
  };

  const { page, setPage, pageSize, setPageSize, skip, limit } = useServerPaged({ initialSize: 20, resetKey: `${view}:${search}` });
  const listQuery = useQuery({
    queryKey: ['admin', 'customers', search, skip, limit],
    queryFn: () => fetchCustomers(search, { skip, limit }),
    placeholderData: keepPreviousData,
    enabled: view === 'registered',
  });
  const bookingsQuery = useQuery({
    queryKey: ['admin', 'customers-from-bookings', search, skip, limit],
    queryFn: () => fetchCustomersFromBookings(search, { skip, limit }),
    placeholderData: keepPreviousData,
    enabled: view === 'all',
  });
  const activeQuery = view === 'registered' ? listQuery : bookingsQuery;
  const pageItems = activeQuery.data?.items || [];
  const pageCount = Math.max(1, Math.ceil((activeQuery.data?.total || 0) / pageSize));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'customers'] });

  const passwordMutation = useMutation({
    mutationFn: () => setCustomerPassword(pwUser.id, password),
    onSuccess: () => {
      setMsg({ ok: `Contraseña actualizada para ${pwUser.email}.`, err: '' });
      setPwUser(null);
      setPassword('');
    },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo cambiar la contraseña.' }),
  });

  const editMutation = useMutation({
    mutationFn: () => updateCustomer(editUser.id, editForm),
    onSuccess: async () => {
      setMsg({ ok: 'Datos del cliente actualizados.', err: '' });
      setEditUser(null);
      await invalidate();
    },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudieron guardar los datos.' }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }) => updateCustomer(id, { is_active }),
    onSuccess: async () => {
      setMsg({ ok: 'Estado de la cuenta actualizado.', err: '' });
      await invalidate();
    },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo actualizar.' }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCustomer(delUser.id, delMode),
    onSuccess: async (res) => {
      const extra =
        delMode === 'purge'
          ? `${res.bookings_deleted || 0} reservas eliminadas`
          : `${res.bookings_anonymized || 0} reservas anonimizadas`;
      setMsg({ ok: `Datos de ${delUser.email} eliminados (${extra}).`, err: '' });
      setDelUser(null);
      setDelConfirm('');
      await invalidate();
    },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo eliminar.' }),
  });

  return (
    <AdminSection
      eyebrow="Admin"
      title="Clientes"
      description="Usuarios registrados con sus datos de alta. Puedes cambiar contraseñas, desactivar cuentas o borrar los datos si el cliente lo solicita."
      actions={(
        <div className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
          {activeQuery.data?.total ?? 0} {view === 'registered' ? 'registrados' : 'clientes'}
        </div>
      )}
    >
      {msg.ok ? <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{msg.ok}</div> : null}
      {msg.err ? <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{msg.err}</div> : null}

      <div className="mb-5 inline-flex rounded-full border border-slate-200 bg-white p-1">
        <button
          type="button"
          onClick={() => setView('registered')}
          className={`rounded-full px-4 py-2 text-sm font-semibold transition ${view === 'registered' ? 'bg-[#14213d] text-white' : 'text-slate-600 hover:text-[#14213d]'}`}
        >
          Cuentas registradas
        </button>
        <button
          type="button"
          onClick={() => setView('all')}
          className={`rounded-full px-4 py-2 text-sm font-semibold transition ${view === 'all' ? 'bg-[#14213d] text-white' : 'text-slate-600 hover:text-[#14213d]'}`}
        >
          Todos los clientes (desde reservas)
        </button>
      </div>
      {view === 'all' ? (
        <p className="mb-5 text-sm text-slate-500">
          Agregado desde las reservas reales por email. Incluye clientes que pagaron sin crear cuenta (la mayoría, hoy) —
          por eso esta lista casi siempre tendrá más gente que "Cuentas registradas".
        </p>
      ) : null}

      <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
        <label className="relative mb-6 block md:w-96">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por email, nombre o teléfono"
            className="w-full rounded-full border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-[#1fa5a3]"
          />
        </label>

        {activeQuery.isError ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">No se pudo cargar la lista de clientes.</div>
        ) : null}

        {view === 'registered' ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  <th className="pb-3 pr-4">Cliente</th>
                  <th className="pb-3 pr-4">Teléfono</th>
                  <th className="pb-3 pr-4">Alta</th>
                  <th className="pb-3 pr-4">Reservas</th>
                  <th className="pb-3 pr-4">Estado</th>
                  <th className="pb-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageItems.map((u) => (
                  <tr key={u.id} className="text-slate-700">
                    <td className="py-4 pr-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-[#14213d]">
                          <UserRound className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-[#14213d]">{u.full_name || 'Sin nombre'}</p>
                          <p className="truncate text-xs text-slate-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 pr-4 text-slate-600">{u.phone || '—'}</td>
                    <td className="py-4 pr-4 text-slate-600">{formatDate(u.created_at)}</td>
                    <td className="py-4 pr-4 text-slate-600">{u.bookings_count}</td>
                    <td className="py-4 pr-4">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                        u.role === 'admin'
                          ? 'bg-[#14213d] text-white'
                          : u.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {u.role === 'admin' ? <ShieldCheck className="h-3 w-3" /> : null}
                        {u.role === 'admin' ? 'Admin' : u.is_active ? 'Activo' : 'Desactivado'}
                      </span>
                    </td>
                    <td className="py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          title="Editar datos"
                          onClick={() => startEdit(u)}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Cambiar contraseña"
                          onClick={() => { setPwUser(u); setPassword(''); setMsg({ ok: '', err: '' }); }}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]"
                        >
                          <KeyRound className="h-4 w-4" />
                        </button>
                        {u.id !== me?.id ? (
                          <>
                            <button
                              type="button"
                              onClick={() => toggleMutation.mutate({ id: u.id, is_active: !u.is_active })}
                              className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]"
                            >
                              {u.is_active ? 'Desactivar' : 'Activar'}
                            </button>
                            <button
                              type="button"
                              title="Eliminar datos (RGPD)"
                              onClick={() => { setDelUser(u); setDelMode('anonymize'); setDelConfirm(''); setMsg({ ok: '', err: '' }); }}
                              className="flex h-9 w-9 items-center justify-center rounded-full border border-red-200 text-red-500 transition hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-slate-500">Tú</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  <th className="pb-3 pr-4">Cliente</th>
                  <th className="pb-3 pr-4">Teléfono</th>
                  <th className="pb-3 pr-4">Reservas</th>
                  <th className="pb-3 pr-4">Ingresos</th>
                  <th className="pb-3 pr-4">Última reserva</th>
                  <th className="pb-3 pr-4">Actividades</th>
                  <th className="pb-3">Cuenta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageItems.map((c) => (
                  <tr key={c.email} className="text-slate-700">
                    <td className="py-4 pr-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-[#14213d]">
                          <UserRound className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-[#14213d]">{c.name || 'Sin nombre'}</p>
                          <p className="truncate text-xs text-slate-500">{c.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 pr-4 text-slate-600">{c.phone || '—'}</td>
                    <td className="py-4 pr-4 text-slate-600">{c.bookings_count}</td>
                    <td className="py-4 pr-4 font-semibold text-[#14213d]">{eur(c.revenue)}</td>
                    <td className="py-4 pr-4 text-slate-600">{formatDate(c.last_booking_at)}</td>
                    <td className="py-4 pr-4 text-slate-600">
                      <span className="truncate" title={c.activities.join(', ')}>
                        {c.activities.slice(0, 2).join(', ')}
                        {c.activities.length > 2 ? ` +${c.activities.length - 2}` : ''}
                      </span>
                    </td>
                    <td className="py-4">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                        c.is_registered ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {c.is_registered ? <ShieldCheck className="h-3 w-3" /> : null}
                        {c.is_registered ? 'Registered' : 'Guest'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination page={page} pageCount={pageCount} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={setPageSize} pageSizes={[20, 50, 100]} />

        {!pageItems.length && !activeQuery.isLoading ? (
          <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
            <UserRound className="mx-auto h-10 w-10 text-[#1fa5a3]" />
            <h3 className="mt-4 text-lg font-semibold text-[#14213d]">Sin clientes</h3>
            <p className="mt-1 text-sm text-slate-500">
              {view === 'registered' ? 'Todavía no hay usuarios registrados con ese criterio.' : 'Todavía no hay reservas con ese criterio.'}
            </p>
          </div>
        ) : null}
      </section>

      {/* Edit customer modal */}
      {editUser ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setEditUser(null)}>
          <div className="w-full max-w-md rounded-[28px] bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-bold text-[#14213d]">Editar cliente</h3>
                <p className="mt-1 text-sm text-slate-500">Todos los datos de la cuenta.</p>
              </div>
              <button type="button" onClick={() => setEditUser(null)} className="text-slate-500 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Nombre</label>
                <input className={`${inputCls} mt-1.5`} value={editForm.full_name}
                  onChange={(e) => setEditForm((c) => ({ ...c, full_name: e.target.value }))} placeholder="Nombre completo" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Email</label>
                <input type="email" className={`${inputCls} mt-1.5`} value={editForm.email}
                  onChange={(e) => setEditForm((c) => ({ ...c, email: e.target.value }))} placeholder="correo@ejemplo.com" />
                <p className="mt-1 text-xs text-slate-500">Es el usuario con el que inicia sesión.</p>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Teléfono</label>
                <input className={`${inputCls} mt-1.5`} value={editForm.phone}
                  onChange={(e) => setEditForm((c) => ({ ...c, phone: e.target.value }))} placeholder="+34 600 000 000" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Rol</label>
                <select
                  className={`${inputCls} mt-1.5`}
                  value={editForm.role}
                  disabled={editUser.id === me?.id}
                  onChange={(e) => setEditForm((c) => ({ ...c, role: e.target.value }))}
                >
                  <option value="user">Cliente</option>
                  <option value="admin">Administrador</option>
                </select>
                {editUser.id === me?.id ? (
                  <p className="mt-1 text-xs text-slate-500">No puedes cambiar tu propio rol.</p>
                ) : null}
              </div>
              <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <input
                  type="checkbox"
                  checked={editForm.is_active}
                  disabled={editUser.id === me?.id}
                  onChange={(e) => setEditForm((c) => ({ ...c, is_active: e.target.checked }))}
                />
                <span className="text-sm font-semibold text-[#14213d]">Cuenta activa</span>
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setEditUser(null)} className="rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600">Cancelar</button>
              <button
                type="button"
                disabled={!editForm.email.trim() || editMutation.isPending}
                onClick={() => editMutation.mutate()}
                className="rounded-full bg-[#1fa5a3] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-50"
              >
                {editMutation.isPending ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Password modal */}
      {pwUser ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPwUser(null)}>
          <div className="w-full max-w-md rounded-[28px] bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-bold text-[#14213d]">Cambiar contraseña</h3>
                <p className="mt-1 text-sm text-slate-500">{pwUser.email}</p>
              </div>
              <button type="button" onClick={() => setPwUser(null)} className="text-slate-500 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Nueva contraseña (8+, letras y números)"
              className={`${inputCls} mt-5`}
            />
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setPwUser(null)} className="rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600">Cancelar</button>
              <button
                type="button"
                disabled={password.length < 8 || passwordMutation.isPending}
                onClick={() => passwordMutation.mutate()}
                className="rounded-full bg-[#1fa5a3] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-50"
              >
                {passwordMutation.isPending ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* GDPR delete modal */}
      {delUser ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDelUser(null)}>
          <div className="w-full max-w-lg rounded-[28px] bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-[#14213d]">Eliminar datos del cliente</h3>
            <p className="mt-1 text-sm text-slate-600">
              Se borrará la cuenta de <strong>{delUser.email}</strong>. Esta acción no se puede deshacer.
            </p>

            <div className="mt-5 space-y-3">
              <label className={`flex cursor-pointer gap-3 rounded-2xl border p-4 ${delMode === 'anonymize' ? 'border-[#1fa5a3] bg-[#f4fbfa]' : 'border-slate-200'}`}>
                <input type="radio" checked={delMode === 'anonymize'} onChange={() => setDelMode('anonymize')} className="mt-1" />
                <span>
                  <span className="block text-sm font-semibold text-[#14213d]">Anonimizar reservas (recomendado)</span>
                  <span className="block text-xs text-slate-500">Borra la cuenta y los datos personales, pero conserva el registro contable de las reservas, que la ley obliga a guardar.</span>
                </span>
              </label>
              <label className={`flex cursor-pointer gap-3 rounded-2xl border p-4 ${delMode === 'purge' ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}>
                <input type="radio" checked={delMode === 'purge'} onChange={() => setDelMode('purge')} className="mt-1" />
                <span>
                  <span className="block text-sm font-semibold text-[#14213d]">Borrado total</span>
                  <span className="block text-xs text-slate-500">Elimina también las reservas y solicitudes. Perderás el histórico contable de este cliente.</span>
                </span>
              </label>
            </div>

            <p className="mt-5 text-sm text-slate-600">Escribe <strong>ELIMINAR</strong> para confirmar:</p>
            <input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} className={`${inputCls} mt-2`} placeholder="ELIMINAR" />

            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setDelUser(null)} className="rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600">Cancelar</button>
              <button
                type="button"
                disabled={delConfirm !== 'ELIMINAR' || deleteMutation.isPending}
                onClick={() => deleteMutation.mutate()}
                className="rounded-full bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Eliminando…' : 'Eliminar datos'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminSection>
  );
}
